import { motion } from "framer-motion";
import { Palette, Download, Star } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { QRCodeCanvas } from "qrcode.react";
import logoOuraTrust from "@/assets/logo-ouratrust-v2.png";

export const PrintReadySection = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: "Plus qu'un QR Code, un objet de design.",
      subtitle: "Un QR Code unique pour toutes vos plateformes. Vos clients scannent et choisissent où ils souhaitent laisser leur avis (Google, Facebook, Yelp, TripAdvisor, Trustpilot).",
      badge: "Kit Premium Inclus",
      mockupTitle: "Choisissez votre plateforme !",
      mockupSubtitle: "Scannez pour partager votre expérience",
      feature1: "Format A4 premium",
      feature2: "PDF haute résolution",
      feature3: "Design personnalisé",
    },
    en: {
      title: "More than a QR Code, a design piece.",
      subtitle: "One QR Code for all your platforms. Your customers scan and choose where they want to leave their review (Google, Facebook, Yelp, TripAdvisor, Trustpilot).",
      badge: "Premium Kit Included",
      mockupTitle: "Choose your platform!",
      mockupSubtitle: "Scan to share your experience",
      feature1: "Premium A4 format",
      feature2: "High-resolution PDF",
      feature3: "Custom design",
    },
  };

  const t = content[language];

  return (
    <section className="py-32 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Table Tent Mockup */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative flex justify-center"
          >
            {/* 3D Table Tent Effect */}
            <div className="relative">
              {/* Shadow */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-4 bg-gradient-to-r from-transparent via-slate-300/50 to-transparent blur-xl" />
              
              {/* Table Tent Card */}
              <motion.div
                initial={{ rotateY: -10 }}
                whileInView={{ rotateY: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative bg-white rounded-3xl shadow-2xl shadow-slate-300/40 p-8 border border-slate-100"
                style={{ 
                  perspective: '1000px',
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* Logo */}
                <div className="flex justify-center mb-6">
                  <img src={logoOuraTrust} alt="OuraTrust" className="h-6 object-contain" width={158} height={24} />
                </div>

                {/* Stars */}
                <div className="flex justify-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={28}
                      className="fill-yellow-400 text-yellow-400"
                    />
                  ))}
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-center text-foreground mb-2">
                  {t.mockupTitle}
                </h3>
                <p className="text-sm text-center text-muted-foreground mb-6">
                  {t.mockupSubtitle}
                </p>

                {/* QR Code */}
                <div className="flex justify-center mb-6">
                  <div className="bg-white p-3 rounded-xl shadow-inner border border-slate-100">
                    <QRCodeCanvas
                      value="https://ouratrust.com/capture"
                      size={140}
                      level="H"
                      fgColor="#1e3a5f"
                      includeMargin={false}
                    />
                  </div>
                </div>

                {/* Multi-Platform Logos */}
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <span className="text-sm font-medium">
                    <span className="text-blue-500">G</span>
                    <span className="text-red-500">o</span>
                    <span className="text-yellow-500">o</span>
                    <span className="text-blue-500">g</span>
                    <span className="text-green-500">l</span>
                    <span className="text-red-500">e</span>
                  </span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-sm font-medium text-blue-600">Facebook</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-sm font-medium text-red-600">Yelp</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-sm font-medium text-green-600">TripAdvisor</span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-sm font-medium text-emerald-600">Trustpilot</span>
                </div>

                {/* Decorative elements */}
                <div className="absolute -top-3 -right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow-lg">
                  <Palette className="text-white" size={12} />
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right: Text */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium">
              <Download size={16} />
              {t.badge}
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
              {t.title}
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
              {t.subtitle}
            </p>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
              {[t.feature1, t.feature2, t.feature3].map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-white rounded-xl p-4 border border-slate-100 shadow-sm"
                >
                  <div className="w-2 h-2 rounded-full bg-accent" />
                  <span className="text-sm font-medium text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
